import { ITheme, IStyle, mergeStyleSets, getTheme, ActionButton } from '@fluentui/react';
import React from 'react';

export enum StepSectionState {
  Untouched,
  EditableTouched,
  UneditableTouched,
  Active,
}

interface ISteppedListSectionProps {
  id: string;
  title: string;
  state: StepSectionState;
  formBusy: boolean;
  onEditSection: (id: string) => void;
}

const getStyleClasses = (theme: ITheme) => {
  const main: IStyle = {
    paddingTop: '0 !important',
    paddingBottom: '0 !important',
    paddingLeft: '0 !important',
    paddingRight: '0 !important',
  };

  const stepList: IStyle = {
    margin: 0,
    padding: 0,
    listStyle: 'none',
    counterReset: 'item',

    '& > li:last-child': {
      borderBottom: 'none',
    },
  };

  const stepNumber: IStyle = {
    display: 'block',
    lineHeight: 32,
    verticalAlign: 'center',
    textAlign: 'center',
    width: 32,
    height: 32,
    borderRadius: '50%',
    content: 'counter(item)',
    fontSize: '1.25em',
    fontWeight: 'bold',
    position: 'absolute',
    top: 14,
    left: 30,
    backgroundColor: theme.palette.neutralQuaternaryAlt,
  };

  const activeStepNumber: IStyle = {
    ...stepNumber,
    color: theme.palette.white,
    backgroundColor: theme.palette.themeSecondary,
  };

  const step: IStyle = {
    position: 'relative',
    display: 'block',
    padding: '10px 30px',
    fontSize: theme.fonts.medium.fontSize,
    borderBottom: `1px solid ${theme.palette.neutralLight}`,
    counterIncrement: 'item',

    '&:before': stepNumber,
  };

  const untouchedStep: IStyle = {
    ...step,
    opacity: 0.4,
    '&:before': stepNumber,
  };
  const touchedStep: IStyle = {
    ...step,
    '&:before': stepNumber,
  };
  const activeStep: IStyle = {
    ...step,
    '&:before': activeStepNumber,
  };

  const stepHeader: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    alignContent: 'space-between',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minHeight: 40,
    paddingLeft: 42,
  };

  const stepTitle: IStyle = {
    fontSize: '1.25em',
    margin: 0,
    padding: 0,
    width: 1,
    flexGrow: 1,
  };

  const contentParagraph: IStyle = {
    fontSize: theme.fonts.medium.fontSize,
    lineHeight: theme.fonts.medium.lineHeight,
    padding: 0,
    margin: 0,
    marginBottom: '0.75rem',
  };
  const content: IStyle = {
    paddingTop: 20,
    paddingBottom: 10,

    '& > button': {
      width: '100%',
    },
    '& > p': contentParagraph,
  };

  return mergeStyleSets({
    main,
    stepList,
    untouchedStep,
    touchedStep,
    activeStep,
    stepHeader,
    stepTitle,
    content,
  });
};

export const SteppedListSection: React.FunctionComponent<ISteppedListSectionProps> = (props) => {
  const theme = getTheme();
  const styles = getStyleClasses(theme);
  const stepStyle = (() => {
    switch(props.state) {
      case StepSectionState.Active: return styles.activeStep;
      case StepSectionState.EditableTouched:
      case StepSectionState.UneditableTouched: return styles.touchedStep;
      default: return styles.untouchedStep;
    }
  })();

  return (
    <li className={stepStyle}>
      <header className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>{props.title}</h2>
        {props.state === StepSectionState.EditableTouched && props.formBusy === false &&
        <ActionButton
          iconProps={{ iconName: 'Edit'}}
          onClick={() => props.onEditSection(props.id)}
        >
          Edit
        </ActionButton>}
      </header>
      {props.state === StepSectionState.Active &&
      <div className={styles.content}>
        {props.children}
      </div>}
    </li>
  );
};
